// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

export type EntornoSw = {
  produccion: boolean
  soportaServiceWorker: boolean
  publicUrl: string
  href: string
  origen: string
  hostname: string
  /** fetch inyectable: el test no necesita tocar `global.fetch`. */
  pedir: (url: string, init?: RequestInit) => Promise<Response>
  /** recarga de la página (service worker no encontrado). */
  recargar: () => void
}

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void
  onUpdate?: (registration: ServiceWorkerRegistration) => void
}

// [::1] is the IPv6 localhost address; 127.0.0.0/8 is localhost for IPv4.
export function esLocalhost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '[::1]' ||
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(hostname)
  )
}

// Ensure service worker exists, and that we really are getting a JS file.
export function esServiceWorkerValido(status: number, contentType: string | null): boolean {
  return !(status === 404 || (contentType != null && contentType.indexOf('javascript') === -1))
}

/**
 * Entorno real del navegador. `register()` lo recibe como parámetro con este
 * valor por defecto, así que el comportamiento en producción no cambia; los
 * tests pueden pasarle uno propio en vez de mutar globals (sustituir
 * `window.location` o `global.fetch` no surte efecto en todos los entornos).
 */
function leerEntorno(): EntornoSw {
  return {
    produccion: process.env.NODE_ENV === 'production',
    soportaServiceWorker: 'serviceWorker' in navigator,
    publicUrl: process.env.PUBLIC_URL || '',
    href: window.location.href,
    origen: window.location.origin,
    hostname: window.location.hostname,
    pedir: (url, init) => fetch(url, init),
    recargar: () => window.location.reload(),
  }
}

export function register(config?: Config, entorno: EntornoSw = leerEntorno()) {
  if (!entorno.produccion || !entorno.soportaServiceWorker) {
    return
  }

  // The URL constructor is available in all browsers that support SW.
  const publicUrl = new URL(entorno.publicUrl, entorno.href)
  if (publicUrl.origin !== entorno.origen) {
    // Our service worker won't work if PUBLIC_URL is on a different origin
    // from what our page is served on. This might happen if a CDN is used to
    // serve assets; see https://github.com/facebook/create-react-app/issues/2374
    return
  }

  window.addEventListener('load', () => {
    const swUrl = `${entorno.publicUrl}/service-worker.js`

    if (esLocalhost(entorno.hostname)) {
      // This is running on a localhost. Let's check if a service worker still exists or not.
      checkValidServiceWorker(swUrl, entorno, config)

      // Add some additional logging to localhost, pointing developers to the
      // service worker/PWA documentation.
      navigator.serviceWorker.ready.then(() => {
        console.log(
          'This web app is being served cache-first by a service ' +
            'worker. To learn more, visit https://bit.ly/CRA-PWA',
        )
      })
    } else {
      // Is not localhost. Just register service worker
      registerValidSW(swUrl, config)
    }
  })
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing
        if (installingWorker == null) {
          return
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://bit.ly/CRA-PWA.',
              )

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration)
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.')

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration)
              }
            }
          }
        }
      }
    })
    .catch(error => {
      console.error('Error during service worker registration:', error)
    })
}

function checkValidServiceWorker(swUrl: string, entorno: EntornoSw, config?: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  entorno
    .pedir(swUrl, {
      headers: { 'Service-Worker': 'script' },
    })
    .then(response => {
      const contentType = response.headers.get('content-type')
      if (!esServiceWorkerValido(response.status, contentType)) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            entorno.recargar()
          })
        })
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config)
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.')
    })
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister()
      })
      .catch(error => {
        console.error(error.message)
      })
  }
}
